import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import {
  CloudDownload as DownloadIcon,
  ErrorOutline as ErrorIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { apiGetSongByUrl, apiScrapeUrl } from "../utils/useDb";

export default function UrlRedirectPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState<"loading" | "scraping" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [extractedUrl, setExtractedUrl] = useState<string>("");

  useEffect(() => {
    // 1. Extract target url from pathname + search
    let raw = location.pathname.replace(/^\/(import|url)\/?/i, "");
    if (location.search) {
      // If query param ?url= or ?target=
      const params = new URLSearchParams(location.search);
      const queryUrl = params.get("url") || params.get("target");
      if (queryUrl) {
        raw = queryUrl;
      } else if (!raw) {
        raw = location.search.slice(1);
      } else {
        raw += location.search;
      }
    }

    if (!raw || !raw.trim()) {
      setStatus("error");
      setErrorMessage("No target URL specified in /import/... path.");
      return;
    }

    let target = raw.trim();
    try {
      target = decodeURIComponent(target);
    } catch {}

    if (!/^https?:\/\//i.test(target)) {
      target = "https://" + target;
    }

    setExtractedUrl(target);

    let isMounted = true;

    async function handleResolveAndOpen() {
      try {
        setStatus("loading");
        // Step 1: Check if song already exists in database
        const existing = await apiGetSongByUrl(target);
        if (!isMounted) return;

        if (existing && existing.id) {
          navigate(`/Song?id=${existing.id}`, { replace: true });
          return;
        }

        // Step 2: Not in database -> scrape and import
        setStatus("scraping");
        const scraped = await apiScrapeUrl(target);
        if (!isMounted) return;

        if (scraped && scraped.id) {
          navigate(`/Song?id=${scraped.id}`, { replace: true });
        } else {
          throw new Error("Scraped song did not return a valid ID");
        }
      } catch (err: any) {
        if (!isMounted) return;
        console.error("Failed to open / import song by URL:", err);
        setStatus("error");
        setErrorMessage(
          err.message || "Unable to scrape or import chord sheet from this URL."
        );
      }
    }

    handleResolveAndOpen();

    return () => {
      isMounted = false;
    };
  }, [location.pathname, location.search, navigate]);

  return (
    <Box
      sx={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "#ffffff",
      }}
    >
      <Container maxWidth="sm">
        <Card
          variant="outlined"
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          }}
        >
          <CardContent>
            {status !== "error" ? (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2.5}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "primary.light",
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 32 }} />
                </Box>
                <CircularProgress size={36} color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  {status === "loading"
                    ? "Locating chord sheet..."
                    : "Importing chords from source..."}
                </Typography>
                {extractedUrl && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      wordBreak: "break-all",
                      bgcolor: "#f8fafc",
                      p: 1,
                      borderRadius: 1,
                      border: "1px solid #e2e8f0",
                      maxWidth: "100%",
                    }}
                  >
                    {extractedUrl}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Please wait a moment while we fetch chords and tabs for you.
                </Typography>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "#fee2e2",
                    color: "#dc2626",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ErrorIcon sx={{ fontSize: 32 }} />
                </Box>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  Failed to open URL
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {errorMessage}
                </Typography>
                {extractedUrl && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ wordBreak: "break-all" }}
                  >
                    Target: {extractedUrl}
                  </Typography>
                )}
                <Box display="flex" gap={1.5} mt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<BackIcon />}
                    onClick={() => navigate("/Explore")}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                  >
                    Back to Explore
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => window.location.reload()}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                  >
                    Retry
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
